import { Button, Stack } from '@mui/material'
import { ArrowLeftIcon, ArrowRightIcon, Loader2 } from 'lucide-react'

import { bulkImportStoreActions } from '../store/bulk-import.store'
import { checkValidNextStepBulkImport } from '../utils/check-valid-next-step-bulk-import'
import { useBulkImportStore } from '../hooks/useBulkImportStore'
import { buildCardTableData } from '../utils/build-card-table-data'
import { type SettingsBulkImport } from '../store/bulk-import.store.types'

import { type CardActivityType, type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import {
  logCloseBulkImport,
  logCreateBulkImportCardsFail,
  logCreateBulkImportCardsSuccess,
} from '@/lib/firebase_analyics/bulk-import-analytics'

const labelsStep = ['Next: Add content', 'Next: Preview & Customize', 'Create Pages']

export function NavigationBulkImport({
  onCardsCreated = async () => {
    return Promise.resolve()
  },
}: {
  onCardsCreated: (props: { cards: CardActivityWithId[]; settings: SettingsBulkImport }) => Promise<void>
}) {
  const setp = useBulkImportStore(store => store.step)
  const cardType = useBulkImportStore(store => store.cardType)
  const draftCards = useBulkImportStore(store => store.cardsDraft)
  const settings = useBulkImportStore(store => store.settings)
  const loading = useBulkImportStore(store => store.loading)
  const cardsTableMap = useBulkImportStore(store => store.cards)

  const isValidNextStep = checkValidNextStepBulkImport({
    step: setp,
    cardType,
    draftCards,
    settings,
  })

  const onNextStep = async () => {
    if (setp === 2) {
      let cardsMapCreated = new Map<string, CardActivityWithId>()

      try {
        bulkImportStoreActions.updateImportBulkStore({
          loading: true,
        })

        const data = await buildCardTableData({
          cardsDraft: draftCards,
          cardType,
          settings,
        })

        if (data) cardsMapCreated = data
      } catch (error) {
        console.error(error)
      } finally {
        bulkImportStoreActions.updateImportBulkStore({
          loading: false,
          cards: cardsMapCreated,
        })
      }
    }

    bulkImportStoreActions.handleNextStep()

    if (setp === 3) {
      bulkImportStoreActions.updateImportBulkStore({
        loading: true,
      })
      const cards = Array.from(cardsTableMap.values())

      try {
        await onCardsCreated({
          cards,
          settings,
        })

        logCreateBulkImportCardsSuccess({
          cardType: cardType as CardActivityType,
          numberOfCards: cards.length,
        })
      } catch {
        logCreateBulkImportCardsFail({
          cardType: cardType as CardActivityType,
          numberOfCards: cards.length,
        })
      }

      bulkImportStoreActions.updateImportBulkStore({
        loading: false,
        cards: new Map(),
        cardsDraft: new Map(),
        step: 1,
        cardType: 'idle',
        selectedCards: [],
      })
      bulkImportStoreActions.toggleBulkImportModal()
    }
  }

  return (
    <Stack
      sx={{
        bgcolor: '#fff',
        paddingX: '20px',
        paddingY: '16px',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        width: '100%',
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: '8px',
          fotnSize: '16px',
          color: '#475467',
        }}
      >
        {setp !== 1 ? (
          <Button startIcon={<ArrowLeftIcon size={20} />} onClick={bulkImportStoreActions.handlePreviousStep}>
            Back
          </Button>
        ) : null}
        <Button
          sx={{
            color: '#B42318',
          }}
          onClick={() => {
            logCloseBulkImport()
            bulkImportStoreActions.toggleBulkImportModal()
          }}
        >
          Cancel
        </Button>
      </Stack>
      <Stack>
        <Button
          disabled={!isValidNextStep || loading}
          endIcon={loading ? <Loader2 className="animate-spin" size={20} /> : <ArrowRightIcon size={20} />}
          sx={{
            marginRight: '80px',
          }}
          variant="contained"
          onClick={onNextStep}
        >
          {labelsStep[setp - 1]}
        </Button>
      </Stack>
    </Stack>
  )
}
